import styled from "styled-components/macro";
import {
  LinearProgress,
  LinearProgressProps,
  Paper,
  Popper,
} from "@mui/material";
import { medBlue, blueNotification } from "../../constants/colors";
import { Z_INDEX } from "../../constants/constants";
import { useRef } from "react";

const LoadingLinearProgressStyles = styled.div`
  z-index: ${Z_INDEX.L10};
  pointer-events: none;
  transition:
    opacity 0.3s cubic-bezier(0.075, 0.82, 0.165, 1),
    height 0.3s cubic-bezier(0.075, 0.82, 0.165, 1) 0.3s; /* delay height transition until after opacity transition */
  opacity: 1;
  height: 6px;
  &.loaded {
    opacity: 0;
    height: 0;
  }
`;

const LoadingLinearProgress = ({
  progressPercent,
  isDeterminate = true,
  numerator,
  denominator,
}: {
  progressPercent: number;
  isDeterminate?: boolean;
  numerator?: number;
  denominator?: number;
}) => {
  if ((numerator && isNaN(numerator)) || (denominator && isNaN(denominator))) {
    return null;
  }
  return (
    <LoadingLinearProgressStyles
      className={`loadingLinearProgress ${progressPercent >= 1 ? "loaded" : ""}`}
    >
      <StyledLinearProgressWithTooltipArrow
        aria-valuemin={0}
        aria-valuemax={100}
        variant={
          // show indeterminate loader until we have at least 1 askr aggregate
          isDeterminate ? "determinate" : "indeterminate"
        }
        {...(isDeterminate
          ? { value: progressPercent * 100, numerator, denominator }
          : {})}
      />
    </LoadingLinearProgressStyles>
  );
};

export default LoadingLinearProgress;

export const StyledLinearProgressWithTooltipArrow = styled(
  LinearProgressWithTooltipArrow
)`
  height: 6px;
  .MuiLinearProgress-colorPrimary {
    background-color: ${blueNotification};
    .MuiLinearProgress-barColorPrimary {
      background-color: ${medBlue};
    }
  }
`;

function LinearProgressWithTooltipArrow(
  props: LinearProgressProps & {
    value?: number;
    numerator?: number;
    denominator?: number;
    explanation?: string;
  }
) {
  const { value, numerator, denominator, explanation } = props;

  const tooltipText =
    numerator && denominator
      ? `${numerator}/${denominator}`
      : `${value?.toFixed(0) ?? "--"}%`;

  const hideTooltip = Boolean(!value || !denominator || isNaN(denominator));
  const ref = useRef<HTMLDivElement>(null);
  if (isNaN(Number(numerator)) || isNaN(Number(denominator))) {
    return null;
  }
  return (
    <LinearProgressWithTooltipArrowStyles ref={ref}>
      <LinearProgress variant={"determinate"} {...props} />
      {hideTooltip || !ref.current ? null : (
        <Popper
          anchorEl={ref.current}
          open={true}
          style={{
            position: "absolute",
            left: `calc(${value}%)`,
            top: -40,
            width: 0,
            display: "flex",
            justifyContent: "center",
          }}
          disablePortal={true}
          placement="top"
        >
          {({ TransitionProps }) => (
            <Paper
              className="popperContainer"
              {...TransitionProps}
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div>{tooltipText}</div>
              {explanation && (
                <div style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                  {"("}
                  {explanation}
                  {")"}
                </div>
              )}
            </Paper>
          )}
        </Popper>
      )}
    </LinearProgressWithTooltipArrowStyles>
  );
}
const LinearProgressWithTooltipArrowStyles = styled.div`
  position: relative;

  .popperContainer {
    width: fit-content;
    position: relative;
    display: flex;
    align-items: center;
    padding: 8px;
    background-color: #fff;
    border-radius: 4px;

    transform: scale(0.75);
    transform-origin: bottom;

    &::before,
    &::after {
      content: "";
      position: absolute;
      width: 0;
      height: 0;
      border: 8px solid transparent;
      left: calc(50% - 8px);
    }

    &::before {
      border-top-color: #fff;
      bottom: -16px;
    }

    &::after {
      border-top-color: #fff;
      bottom: -14px;
    }
  }
`;
